/** Per-level visual themes for the 10 story chapters of "BlueBob and the Missing
 * Treasure Patties". Each theme is a complete art-direction config — it drives the maze
 * wall palette, floor, prop kit, AND the Environment (fog / sky gradient / lighting), so a
 * level reads as a distinct location, not just a recolour.
 *
 * Story locations (Chapters 1-10):
 *  1 Jelly Trail · 2 Coral Cave · 3 Seaweed Maze · 4 Bubble Bridge · 5 Sunken Snack Ship
 *  6 Deep Sea Drop · 7 Patty Reef · 8 Stormy Jelly Skies · 9 Royal Jelly Cave · 10 Showdown
 */

/** Which decorative prop family the maze scatters for this location. */
export type PropKit =
  | 'reef' // bright coral + seaweed (jelly fields)
  | 'cave' // glowing crystals + rock (coral cave)
  | 'seaweed' // dense tall kelp (seaweed maze)
  | 'bubble' // floating bubble clusters (bubble bridge)
  | 'ship' // barrels + planks + crates (sunken ship)
  | 'abyss' // sparse bioluminescent stalks (deep sea)
  | 'patty' // coral + golden treasure patties (patty reef)
  | 'storm' // jagged coral towers (stormy skies)
  | 'royal' // glowing jelly pillars (royal cave)
  | 'throne' // crystals + coin piles (showdown)

export interface LevelTheme {
  level: number
  /** Chapter name, surfaced on the level banner + cutscene. */
  name: string
  /** Wall instance HSL base (hue 0-1). Per-cell jitter is added on top. */
  wall: { hue: number; sat: number; light: number }
  /** Seabed floor tint. */
  floorColor: number
  /** Depth fog. */
  fog: { color: number; density: number }
  /** Sky-dome gradient (top→bottom). */
  sky: { top: number; mid: number; bottom: number }
  /** Lighting rig colours + key intensity. */
  light: { key: number; keyIntensity: number; hemiSky: number; hemiGround: number; ambient: number }
  /** Glow accent colour for emissive props. */
  accent: number
  propKit: PropKit
}

export const LEVEL_COUNT = 10

export const LEVEL_THEMES: LevelTheme[] = [
  {
    level: 1,
    name: 'The Jelly Trail',
    wall: { hue: 0.52, sat: 0.5, light: 0.42 },
    floorColor: 0xcdb98a,
    fog: { color: 0x07344a, density: 0.018 },
    sky: { top: 0x1a8f9e, mid: 0x0e5f78, bottom: 0x05202f },
    light: { key: 0xfff4d6, keyIntensity: 1.6, hemiSky: 0x9fe9ff, hemiGround: 0x06283a, ambient: 0x2b6076 },
    accent: 0xff86c4,
    propKit: 'reef',
  },
  {
    level: 2,
    name: 'Coral Cave Trouble',
    wall: { hue: 0.77, sat: 0.46, light: 0.32 },
    floorColor: 0x2a1f3a,
    fog: { color: 0x150a2a, density: 0.036 },
    sky: { top: 0x3a2470, mid: 0x1e1142, bottom: 0x0a0518 },
    light: { key: 0xb98cff, keyIntensity: 1.1, hemiSky: 0x9a6cff, hemiGround: 0x0a0518, ambient: 0x3a2a6a },
    accent: 0xc04bff,
    propKit: 'cave',
  },
  {
    level: 3,
    name: 'The Seaweed Maze',
    wall: { hue: 0.3, sat: 0.55, light: 0.36 },
    floorColor: 0x2f3a18,
    fog: { color: 0x12361a, density: 0.03 },
    sky: { top: 0x3f9e5a, mid: 0x1c5f32, bottom: 0x07200f },
    light: { key: 0xe8ffc6, keyIntensity: 1.4, hemiSky: 0xbfffa0, hemiGround: 0x10300f, ambient: 0x3a6030 },
    accent: 0x7cff4d,
    propKit: 'seaweed',
  },
  {
    level: 4,
    name: 'Bubble Bridge',
    wall: { hue: 0.55, sat: 0.6, light: 0.5 },
    floorColor: 0x8fcad6,
    fog: { color: 0x2a6f8a, density: 0.02 },
    sky: { top: 0x6fe0ff, mid: 0x2f9fd0, bottom: 0x0e5070 },
    light: { key: 0xffffff, keyIntensity: 1.8, hemiSky: 0xcdf4ff, hemiGround: 0x2a6f8a, ambient: 0x4f90b0 },
    accent: 0xeaffff,
    propKit: 'bubble',
  },
  {
    level: 5,
    name: 'The Sunken Snack Ship',
    wall: { hue: 0.08, sat: 0.45, light: 0.34 },
    floorColor: 0x4a3420,
    fog: { color: 0x2a1c10, density: 0.03 },
    sky: { top: 0x8a6a3a, mid: 0x4f3820, bottom: 0x1a1008 },
    light: { key: 0xffcf8a, keyIntensity: 1.3, hemiSky: 0xffd9a0, hemiGround: 0x2a1c10, ambient: 0x5a4030 },
    accent: 0xffb347,
    propKit: 'ship',
  },
  {
    level: 6,
    name: 'Deep Sea Drop',
    wall: { hue: 0.62, sat: 0.55, light: 0.26 },
    floorColor: 0x0a1430,
    fog: { color: 0x030a1e, density: 0.05 },
    sky: { top: 0x12306a, mid: 0x081636, bottom: 0x01030c },
    light: { key: 0x6fb0ff, keyIntensity: 0.95, hemiSky: 0x4f8cff, hemiGround: 0x01030c, ambient: 0x1a2a5a },
    accent: 0x37e0ff,
    propKit: 'abyss',
  },
  {
    level: 7,
    name: 'The Patty Reef',
    wall: { hue: 0.95, sat: 0.62, light: 0.46 },
    floorColor: 0xe0c08a,
    fog: { color: 0x2a5f6a, density: 0.018 },
    sky: { top: 0x2fd0c0, mid: 0x1f9fb0, bottom: 0x0e5060 },
    light: { key: 0xfff0d0, keyIntensity: 1.7, hemiSky: 0xfff0d0, hemiGround: 0x2a5f6a, ambient: 0x4f8080 },
    accent: 0xffcf3a,
    propKit: 'patty',
  },
  {
    level: 8,
    name: 'Stormy Jelly Skies',
    wall: { hue: 0.6, sat: 0.32, light: 0.34 },
    floorColor: 0x223040,
    fog: { color: 0x121c2a, density: 0.034 },
    sky: { top: 0x3a4f6a, mid: 0x1e2c40, bottom: 0x0a1018 },
    light: { key: 0xdfe8ff, keyIntensity: 1.2, hemiSky: 0x9fb4d0, hemiGround: 0x121c2a, ambient: 0x33445a },
    accent: 0xffe14d,
    propKit: 'storm',
  },
  {
    level: 9,
    name: 'Royal Jelly Cave',
    wall: { hue: 0.81, sat: 0.55, light: 0.4 },
    floorColor: 0x2a1838,
    fog: { color: 0x1a0a30, density: 0.032 },
    sky: { top: 0x7a3aff, mid: 0x4a1f9e, bottom: 0x140530 },
    light: { key: 0xffe9a0, keyIntensity: 1.4, hemiSky: 0xc78cff, hemiGround: 0x1a0a30, ambient: 0x4a2a7a },
    accent: 0xffd24d,
    propKit: 'royal',
  },
  {
    level: 10,
    name: 'The Jelly King Showdown',
    wall: { hue: 0.02, sat: 0.58, light: 0.38 },
    floorColor: 0x3a1418,
    fog: { color: 0x2a0810, density: 0.03 },
    sky: { top: 0xff5a4d, mid: 0x9e1f2f, bottom: 0x2a0510 },
    light: { key: 0xffd24d, keyIntensity: 1.7, hemiSky: 0xffb38a, hemiGround: 0x2a0810, ambient: 0x6a2a30 },
    accent: 0xffd24d,
    propKit: 'throne',
  },
]

/** Theme for a level (1-based), clamped/wrapped past 10 (endless safety). */
export function getLevelTheme(level: number): LevelTheme {
  const idx = (Math.max(1, level) - 1) % LEVEL_THEMES.length
  return LEVEL_THEMES[idx]
}
